import {
  call,
  put,
  take,
  takeEvery,
  select,
  takeLeading,
  race,
  delay,
} from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";
import Debug from "debug";
import axios from "axios";

import { ACTIONS } from "../actions/constants";
import { extractResponse } from "./helpers";
import {
  ErrorState,
  Stream,
  RequestResponse,
  ActionChannelGetOne,
  ActionBroadcastSettings,
  BroadcastSettings,
  ActionViewerCountStartPoll,
  ActionViewerVerifyPassword,
  ActionViewerRefreshToken,
  FullAuthResponse,
  ActionViewerAuthRequest,
  ViewerAuthResponse,
  ActionViewerReaction,
  FullRequestResponse,
  ActionError,
} from "../types";

import { RootState } from "../reducers";
import { getErrorObj } from "../utils/errorUtils";

const debug = Debug("broadcast:sagas:viewer");

const API_URL = process.env.REACT_APP_API_BASE_URL;

const STREAM_STATE_POLLING_INTERVAL = 10000;
const VIEWER_COUNT_CHECK_INTERVAL = 30000;
const MAX_STREAM_STATE_CHECK_COUNT = 100000;
const REFRESH_WINDOW = 10000;

const channelGetOneEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const broadcastSettingsEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string =>
  `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/settings`;

const playbackUrlEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/url`;

const streamGetStateEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/state`;

const streamGetCountEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string =>
  `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/viewer-count`;

const verifyPasswordEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string =>
  `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/verifyPassword`;

const refreshTokenEndpoint = (): string => `${API_URL}/auth/session/refresh`;

const reactToStreamEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/react`;

const viewerAuthEndpoint = (
  provider: string,
  code: string,
  orgId: string,
  spaceId: string,
  arn: string
): string =>
  `${API_URL}/auth/${provider}?code=${code}&orgId=${orgId}&spaceId=${spaceId}&arn=${arn}`;

const getViewerToken = (state: RootState): string => state.viewer;
const getStudioToken = (state: RootState): string => state.studio;

function* broadcastSettings({
  payload,
  logger,
}: ActionBroadcastSettings): SagaIterator {
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const viewer = yield select(getViewerToken);
    const { token } = viewer;

    const studio = yield select(getStudioToken);
    const { token: studioToken } = studio;
    debug("studio token", studioToken);

    const result: RequestResponse = yield call(
      axios.get,
      broadcastSettingsEndpoint(orgId, spaceId, channelId),
      {
        headers: {
          Authorization: `Bearer ${
            token !== null ? token : studioToken !== null ? studioToken : null
          }`,
        },
      }
    );
    debug("broadcastSettings result", result);
    const data = extractResponse(result.data);
    if (data) {
      const settings: BroadcastSettings = data.settings;
      yield put({
        type: ACTIONS.VIEWER_GET_BROADCAST_SETTINGS_SUCCESS,
        payload: settings,
      });
    } else {
      debug("broadcastSettings failure");
      logger?.error("Failed to get broadcast settings (no response data)", {
        channelId: payload.channelId,
        result,
      });
      yield put({
        type: ACTIONS.VIEWER_GET_BROADCAST_SETTINGS_FAILURE,
        payload: { error: ErrorState.FailedToFetchBroadcastSettings },
      });
    }
  }
}

function* playbackUrl({
  payload,
  logger,
}: ActionBroadcastSettings): SagaIterator {
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const viewer = yield select(getViewerToken);
    const { token } = viewer;

    const studio = yield select(getStudioToken);
    const { token: studioToken } = studio;
    debug("studio token", studioToken);

    const result: RequestResponse = yield call(
      axios.get,
      playbackUrlEndpoint(orgId, spaceId, channelId),
      {
        headers: {
          Authorization: `Bearer ${
            token !== null ? token : studioToken !== null ? studioToken : null
          }`,
        },
      }
    );
    debug("playbackUrl result", result);
    const data = extractResponse(result.data);
    if (data) {
      const playbackUrl: string = data.playbackUrl;
      yield put({
        type: ACTIONS.VIEWER_GET_PLAYBACK_URL_SUCCESS,
        payload: playbackUrl,
      });
    } else {
      debug("playbackUrl failure");
      logger?.error("Failed to get playback url (no response data)", {
        channelId: payload.channelId,
        result,
      });
      yield put({
        type: ACTIONS.VIEWER_GET_PLAYBACK_URL_FAILURE,
        payload: { error: ErrorState.FailedToFetchPlaybackUrl },
      });
    }
  }
}

function* channelsGetOne({
  payload,
  logger,
}: ActionChannelGetOne): SagaIterator {
  debug("channelsGetOne", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const viewer = yield select(getViewerToken);
    const { token } = viewer;

    let headers = {};

    if (token !== null) {
      headers = { Authorization: `Bearer ${token}` };
    }

    try {
      const result: RequestResponse = yield call(
        axios.get,
        channelGetOneEndpoint(orgId, spaceId, channelId),
        {
          headers,
        }
      );
      debug("get channel result", result);
      const data = extractResponse(result.data);
      if (data) {
        const channel: Stream = data.channel;
        yield put({
          type: ACTIONS.VIEWER_GET_CHANNEL_SUCCESS,
          payload: channel,
        });
      } else {
        debug("failure");
        logger?.error("Failed to get channelsGetOne (no response data)", {
          channelId: payload.channelId,
          result,
        });
        yield put({
          type: ACTIONS.VIEWER_GET_CHANNEL_FAILURE,
          payload: { error: ErrorState.FailedToFetchStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to get channelsGetOne (catch)", {
        channelId: payload.channelId,
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.VIEWER_GET_CHANNEL_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

export function* getStreamState({
  payload,
  logger,
}: ActionChannelGetOne): SagaIterator {
  debug("getStreamState", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    let streamStateCheckCount = 0;
    while (true) {
      try {
        const result: RequestResponse = yield call(
          axios.get,
          streamGetStateEndpoint(orgId, spaceId, channelId)
        );
        debug("stream state result", result);
        const data = extractResponse(result.data);
        const hasError = !data || data.state !== "UNKNOWN"; // ignore unknown status temporary fix for too many requests
        if (data && data.state !== "UNKNOWN") {
          streamStateCheckCount++;
          yield put({
            type: ACTIONS.VIEWER_STREAM_STATE_SUCCESS,
            payload: data.state,
          });
        } else if (hasError) {
          logger?.error("Failed to fecth stream state (no response data)", {
            channelId: payload.channelId,
            result,
          });
          yield put({
            type: ACTIONS.VIEWER_STREAM_STATE_FAILURE,
            payload: { error: ErrorState.FailedToFetchStreamState },
          });
        }
        if (streamStateCheckCount < MAX_STREAM_STATE_CHECK_COUNT) {
          yield delay(STREAM_STATE_POLLING_INTERVAL);
        } else {
          yield put({
            type: ACTIONS.VIEWER_STREAM_STATE_POLLING_END,
            payload,
          });
        }
      } catch (error) {
        logger?.error("Failed to fecth stream state (catch)", {
          channelId: payload.channelId,
          error: getErrorObj(error),
        });
        yield put({
          type: ACTIONS.VIEWER_STREAM_STATE_FAILURE,
          payload: { error: ErrorState.FailedToFetchStreamState },
        });
        yield delay(STREAM_STATE_POLLING_INTERVAL);
      }
    }
  }
}

export function* getStreamCount({
  payload,
  logger,
}: ActionViewerCountStartPoll): SagaIterator {
  debug("getStreamCount", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId, arn } = payload;
    while (true) {
      try {
        const result: RequestResponse = yield call(
          axios.post,
          streamGetCountEndpoint(orgId, spaceId, channelId),
          JSON.stringify({ arn })
        );
        debug("stream count result", result);
        const data = extractResponse(result.data);
        if (data) {
          yield put({
            type: ACTIONS.VIEWER_COUNT_SUCCESS,
            payload: data.viewerCount,
          });
        } else {
          logger?.error(
            "Failed to get stream viewer count (no response data)",
            {
              channelId: payload.channelId,
              result,
            }
          );
          // We dont care if we cant get the viewer count
        }

        yield delay(VIEWER_COUNT_CHECK_INTERVAL);
      } catch (error) {
        logger?.error("Failed to get stream viewer count (catch)", {
          channelId: payload.channelId,
          error: getErrorObj(error),
        });
        // We dont care if we cant get the viewer count
        yield put({
          type: ACTIONS.VIEWER_COUNT_POLLING_END,
          payload,
        });
        yield delay(VIEWER_COUNT_CHECK_INTERVAL);
      }
    }
  }
}

export function* getVerifyPassword({
  payload,
}: ActionViewerVerifyPassword): SagaIterator {
  debug("getVerifyPassword", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId, arn, password } = payload;
    try {
      const result: RequestResponse = yield call(
        axios.post,
        verifyPasswordEndpoint(orgId, spaceId, channelId),
        { hashedPassword: password, arn }
      );
      debug("getVerifyPassword result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.VIEWER_VERIFY_PASSWORD_SUCCESS,
          payload: data.tokens,
        });
        yield put({
          type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_START,
          payload: null,
        });
      } else {
        yield put({
          type: ACTIONS.VIEWER_VERIFY_PASSWORD_FAILURE,
          payload: { error: ErrorState.FailedToVerifyPassword },
        });
      }
    } catch (error) {
      yield put({
        type: ACTIONS.VIEWER_VERIFY_PASSWORD_FAILURE,
        payload: { error: ErrorState.FailedToVerifyPassword },
      });
    }
  }
}

export function* refreshTokenPolling({
  payload,
  logger,
}: ActionViewerRefreshToken): SagaIterator {
  debug("refreshTokenPolling", payload);
  const viewer = yield select(getViewerToken);
  const { refresh, channel } = viewer;

  const { orgId, spaceId } = channel;
  const { token, expires } = refresh;
  debug("channel", orgId, spaceId);
  debug("refresh token", token, expires);
  while (true) {
    try {
      if (expires !== null) {
        const now = Date.now();
        const duration = expires - now - REFRESH_WINDOW; // attempt 10 seconds before refresh expires
        debug(`will delay refresh for ${duration} ms`);
        yield delay(duration);
        const result: FullAuthResponse = yield call(
          axios.post,
          refreshTokenEndpoint(),
          { orgId, spaceId },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        debug("refresh result", result);
        yield put({
          type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_SUCCESS,
          payload: result.data,
        });
      } else {
        yield put({
          type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_FAILURE,
          payload: { error: ErrorState.FailedToRefreshToken },
        });
        yield put({
          type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_END,
          payload: null,
        });
      }
    } catch (error) {
      logger?.error("Failed to polling refresh token (catch)", {
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_FAILURE,
        payload: { error: ErrorState.FailedToRefreshToken },
      });
      yield put({
        type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_END,
        payload: null,
      });
    }
  }
}

function* viewerAuthGet({ payload }: ActionViewerAuthRequest): SagaIterator {
  if (payload !== undefined) {
    const { provider, code, orgId, spaceId, arn } = payload;

    try {
      const result: ViewerAuthResponse = yield call(
        axios.get,
        viewerAuthEndpoint(provider, code, orgId, spaceId, arn)
      );
      debug("get editor auth result", result.data.data);

      const { success, data } = result.data;

      if (success) {
        yield put({
          type: ACTIONS.VIEWER_AUTH_SUCCESS,
          payload: data,
        });
        yield put({
          type: ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_START,
          payload: null,
        });
      }
    } catch (error) {
      debug(error);
      yield put({
        type: ACTIONS.VIEWER_AUTH_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

function* viewerReactToStream({
  payload,
  logger,
}: ActionViewerReaction): SagaIterator {
  if (payload !== undefined) {
    const { orgId, spaceId, channelId, arn, reactionData } = payload;

    try {
      const result: FullRequestResponse = yield call(
        axios.post,
        reactToStreamEndpoint(orgId, spaceId, channelId),
        JSON.stringify({ reactionData, arn })
      );
      debug("viewerReactToStream result", result);
      const data = extractResponse(result.data);
      if (result.status === 200) {
        yield put({
          type: ACTIONS.VIEWER_REACT_TO_STREAM_SUCCESS,
          payload: data,
        });
      } else {
        logger?.error(
          "Failed to send viewer reaction to stream (bad response)",
          {
            channelId: payload.channelId,
            result,
          }
        );
        yield put({
          type: ACTIONS.VIEWER_REACT_TO_STREAM_FAILURE,
          payload: { error: ErrorState.FailedToReactToStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to send viewer reaction to stream (catch)", {
        channelId: payload.channelId,
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.VIEWER_REACT_TO_STREAM_FAILURE,
        payload: { error: ErrorState.FailedToReactToStream },
      });
    }
  }
}

function* removeError({ payload }: ActionError): SagaIterator {
  if (payload !== undefined) {
    yield delay(5000);
    yield put({
      type: ACTIONS.EDITOR_REMOVE_ERROR_CONFIRM,
      payload,
    });
  }
}

export function* viewerChannelsGetOneSaga(): SagaIterator {
  yield takeEvery(ACTIONS.VIEWER_GET_CHANNEL_REQUEST, channelsGetOne);
}

export function* viewerBroadcastSettingsSaga(): SagaIterator {
  yield takeLeading(
    ACTIONS.VIEWER_GET_BROADCAST_SETTINGS_REQUEST,
    broadcastSettings
  );
}

export function* viewerPlaybackUrlSaga(): SagaIterator {
  yield takeLeading(ACTIONS.VIEWER_GET_PLAYBACK_URL_REQUEST, playbackUrl);
}

export function* viewerStreamState(): SagaIterator {
  while (true) {
    const action = yield take(ACTIONS.VIEWER_STREAM_STATE_POLLING_START);
    yield race([
      call(getStreamState, action),
      take(ACTIONS.VIEWER_STREAM_STATE_POLLING_END),
    ]);
  }
}

export function* viewerStreamCount(): SagaIterator {
  while (true) {
    const action = yield take(ACTIONS.VIEWER_COUNT_POLLING_START);
    yield race([call(getStreamCount, action)]);
  }
}

export function* viewerVerifyPassword(): SagaIterator {
  yield takeLeading(ACTIONS.VIEWER_VERIFY_PASSWORD_REQUEST, getVerifyPassword);
}

export function* viewerRefreshToken(): SagaIterator {
  while (true) {
    const action = yield take(ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_START);
    yield race([
      call(refreshTokenPolling, action),
      take(ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_END),
    ]);
  }
}

export function* viewerAuth(): SagaIterator {
  yield takeEvery(ACTIONS.VIEWER_AUTH_REQUEST, viewerAuthGet);
}

export function* reactToStream(): SagaIterator {
  yield takeLeading(
    ACTIONS.VIEWER_REACT_TO_STREAM_REQUEST,
    viewerReactToStream
  );
}

export function* removeErrorMessage(): SagaIterator {
  yield takeEvery(ACTIONS.EDITOR_REMOVE_ERROR, removeError);
}
